import React from 'react'
import { Card, Flex, Text } from '@tremor/react'
import { cn } from '@/lib/utils'
import { NumberTicker } from '@/components/magicui'
import type { StatusLevel } from '@/styles/design-tokens'

interface StatusCardProps {
  title: string
  value: string | number
  unit?: string
  subtitle?: string
  secondary?: string
  status?: StatusLevel
  statusLabel?: string
  progress?: {
    value: number
    max?: number
  }
  icon: React.ReactNode
  inverted?: boolean  // For metrics where lower is better (missed calls)
  animated?: boolean  // Enable NumberTicker animation
  decimalPlaces?: number  // Decimal places for animated numbers
  className?: string
}

const statusStyles: Record<StatusLevel, {
  border: string
  badge: string
  icon: string
  progress: string
  bg: string
  borderOuter: string
}> = {
  elite: {
    border: 'border-l-violet-500',
    badge: 'bg-violet-600 text-white',
    icon: 'bg-violet-100 text-violet-600',
    progress: 'bg-violet-500',
    bg: 'bg-gradient-to-br from-violet-50/50 via-white to-white',
    borderOuter: 'border-violet-100',
  },
  strong: {
    border: 'border-l-emerald-500',
    badge: 'bg-emerald-600 text-white',
    icon: 'bg-emerald-100 text-emerald-600',
    progress: 'bg-emerald-500',
    bg: 'bg-gradient-to-br from-emerald-50/50 via-white to-white',
    borderOuter: 'border-emerald-100',
  },
  approaching: {
    border: 'border-l-amber-500',
    badge: 'bg-amber-600 text-white',
    icon: 'bg-amber-100 text-amber-600',
    progress: 'bg-amber-500',
    bg: 'bg-gradient-to-br from-amber-50/50 via-white to-white',
    borderOuter: 'border-amber-100',
  },
  danger: {
    border: 'border-l-red-500',
    badge: 'bg-red-600 text-white',
    icon: 'bg-red-100 text-red-600',
    progress: 'bg-red-500',
    bg: 'bg-gradient-to-br from-red-50/50 via-white to-white',
    borderOuter: 'border-red-100',
  },
  neutral: {
    border: 'border-l-blue-500',
    badge: 'bg-blue-600 text-white',
    icon: 'bg-blue-100 text-blue-600',
    progress: 'bg-blue-500',
    bg: 'bg-gradient-to-br from-blue-50/30 via-white to-white',
    borderOuter: 'border-blue-100',
  },
}

export function StatusCard({
  title,
  value,
  unit = '',
  subtitle,
  secondary,
  status = 'neutral',
  statusLabel,
  progress,
  icon,
  inverted = false,
  animated = true,
  decimalPlaces = 0,
  className,
}: StatusCardProps) {
  const styles = statusStyles[status]

  return (
    <Card
      className={cn(
        "relative border-l-4 shadow-md hover:shadow-lg",
        styles.border,
        styles.bg,
        "border-t border-r border-b",
        styles.borderOuter,
        "hover:-translate-y-0.5 transition-all duration-200",
        "animate-fade-in",
        className
      )}
    >
      {/* Header Row */}
      <Flex justifyContent="between" alignItems="start">
        <div className="flex-1 min-w-0">
          <Text className="text-zinc-500 text-sm font-medium uppercase tracking-wide">
            {title}
          </Text>

          <Flex alignItems="baseline" className="gap-1 mt-2">
            {animated && typeof value === 'number' ? (
              <NumberTicker
                value={value}
                decimalPlaces={decimalPlaces}
                className="font-mono text-4xl font-black text-zinc-950 tracking-tight"
              />
            ) : (
              <span className="font-mono text-4xl font-black text-zinc-950 tracking-tight">
                {value}
              </span>
            )}
            {unit && (
              <span className="text-zinc-500 text-lg font-medium">{unit}</span>
            )}
          </Flex>
        </div>

        {/* Icon */}
        <div className={cn(
          "w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0",
          styles.icon
        )}>
          {icon}
        </div>
      </Flex>

      {/* Progress Bar (optional) */}
      {progress && (
        <div className="mt-4">
          <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
            <div
              className={cn(
                "h-full rounded-full transition-all duration-500",
                styles.progress
              )}
              style={{
                width: `${Math.min((progress.value / (progress.max || 100)) * 100, 100)}%`
              }}
            />
          </div>
        </div>
      )}

      {/* Footer Row */}
      <Flex justifyContent="between" alignItems="center" className="mt-4">
        <div>
          {subtitle && (
            <Text className="text-zinc-600 text-sm">{subtitle}</Text>
          )}
          {secondary && (
            <Text className="text-zinc-400 text-xs mt-0.5">{secondary}</Text>
          )}
        </div>

        {statusLabel && (
          <span className={cn(
            "px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide shadow-sm",
            styles.badge
          )}>
            {statusLabel}
          </span>
        )}
      </Flex>
    </Card>
  )
}
